<template>
  <div class="base-avatar-wrapper">
    <!-- Avatar with image -->
    <q-avatar
      v-if="imageUrl"
      :size="props.size"
      :class="{ 'tw-mr-1': showName, [props.shape]: props.shape !== 'circle' }"
      class="base-avatar"
    >
      <img
        :class="{
          'tw-border-3 tw-border-transparent': hasBorder,
          [`tw-border-${borderColor}`]: borderColor,
        }"
        :src="imageUrl"
      />
    </q-avatar>

    <!-- DevStride logo -->
    <div
      v-else-if="props.isDevstride"
      class="tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-2"
      :class="{ 'tw-mr-1': props.showName }"
      :style="{
        width: size,
        height: size,
      }"
    >
      <img
        src="/devstride_icon.png"
        alt="Devstride Logo"
        class="tw-inline-block tw-size-5/6"
      />
    </div>

    <!-- Fallback icon -->
    <span
      v-else
      :class="{
        'tw-border-3 tw-border-transparent': hasBorder,
        [`tw-border-${borderColor}`]: borderColor,
      }"
      :style="{
        borderRadius: shapeToRadius[props.shape],
      }"
    >
      <span
        :style="{
          width: hasBorder ? `calc(${size} - .3rem)` : size,
          height: hasBorder ? `calc(${size} - .3rem)` : size,
          borderRadius: shapeToRadius[props.shape],
        }"
        class="no-avatar-wrapper"
      >
        <octicon class="no-avatar-icon" :icon="fallbackIcon" />
      </span>
    </span>

    <!-- Label text -->
    <span
      v-if="showName && label"
      :class="{
        'base-avatar__label--light': props.lightText,
        'tw-ml-2': !imageUrl && !props.isDevstride,
      }"
      class="base-avatar__label"
    >
      {{ label }}
    </span>

    <!-- Loading indicator - positioned over the avatar -->
    <div
      v-if="loading"
      class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-white/70"
      :style="{ width: size, height: size }"
    >
      <p-inner-loader :visible="loading" :size="size" />
    </div>

    <!-- Tooltip -->
    <q-tooltip
      v-if="props.showTooltip && (label || props.tooltipText)"
      :delay="300"
      :offset="[3, 3]"
      anchor="top middle"
      self="bottom middle"
    >
      <span class="tw-text-xs">
        {{ props.tooltipText || label }}
      </span>
    </q-tooltip>
  </div>
</template>

<script setup lang="ts">
import { defineProps, withDefaults } from 'vue';
import { Colors } from '@/api';

type AvatarShape = 'circle' | 'square' | 'rounded';

interface Props {
  /** URL of the avatar image */
  imageUrl?: string;
  /** Label text to display next to avatar */
  label?: string;
  /** Show label text next to avatar */
  showName?: boolean;
  /** Use lighter text color for label */
  lightText?: boolean;
  /** Show loading spinner */
  loading?: boolean;
  /** Avatar size (e.g., '24px', '32px') */
  size?: string;
  /** Add border around avatar */
  hasBorder?: boolean;
  /** Border color */
  borderColor?: Colors;
  /** Show tooltip on hover */
  showTooltip?: boolean;
  /** Custom tooltip text (defaults to label) */
  tooltipText?: string;
  /** If true, will use the devstride logo as the avatar */
  isDevstride?: boolean;
  /** Avatar shape */
  shape?: AvatarShape;
  /** Fallback icon name (Octicon) */
  fallbackIcon?: string;
}

const props = withDefaults(defineProps<Props>(), {
  hasBorder: false,
  showTooltip: false,
  shape: 'circle',
  fallbackIcon: 'person',
  size: '32px',
});

const shapeToRadius: Record<AvatarShape, string> = {
  circle: '50%',
  square: '0',
  rounded: '0.375rem',
};
</script>

<style lang="scss" scoped>
.base-avatar-wrapper {
  @apply tw-flex tw-items-center tw-relative;
}

.base-avatar {
  @apply tw-rounded-full;

  &.square {
    @apply tw-rounded-none;
  }

  &.rounded {
    @apply tw-rounded-md;
  }
}

.base-avatar__label {
  @apply tw-text-sm;

  &--light {
    @apply tw-text-gray-dark;
  }
}

.no-avatar-wrapper {
  @apply tw-flex tw-items-center tw-justify-center tw-bg-gray-light tw-text-gray-dark;
  display: inline-flex;
}

.no-avatar-icon {
  @apply tw-h-4 tw-w-4;
}
</style>
